import React, { useCallback, useContext, useEffect } from "react";
import { FC, useReducer } from "react";
import { useSelector } from "react-redux";
import {
  useMediaQuery,
  Theme,
  makeStyles,
  Divider,
  alpha,
} from "@material-ui/core";
import {
  useTranslate,
  // DashboardMenuItem,
  MenuItemLink,
  DashboardMenuItem,
} from "react-admin";

import SubMenu from "./SubMenu";
import { AppState } from "../../common/types";
import hos from "../hos";
import drivers from "../hos/drivers";
import logs from "../hos/logs";
// import locations from "../locations";
import violations from "../hos/violations";
import unidentifiedDriving from "../hos/unidentified-driving";
import eldDisconnections from "../hos/eld-disconnections";
import fmcsaDataTransfer from "../hos/fmcsa-data-transfer";
import { useLocation } from "react-router-dom";
import trailers from "../assets/trailers-view";
import dvir from "../assets/dvir";
import malfunctionsDiag from "../assets/malfunctions-diag";
import assets from "../assets";
import reports from "../reports";
import administration from "../administration";
import resourceNames from "../../common/data/resourceNames";
import companies from "../administration/companies";
import fleetManagers from "../administration/fleet-managers";
import adminDrivers from "../administration/admin-drivers";
import "./Menu.css";
import vehicles from "../administration/vehicles";
import groups from "../administration/groups";
import permissionNames from "../../common/auth/access-control/permissionNames";
import roles from "../administration/roles";
// import locationsCurrent from "../locations/locations-current";
// import locationsHistory from "../locations/locations-history";
import elds from "../master/elds";
import subscriptions from "../master/subscriptions";
import adminElds from "../administration/admin-elds";
import notifications from "../administration/notifications";
import maintenance from "../assets/maintenance";
import dvirForms from "../administration/dvir-forms";
import eldMalfunctions from "../hos/eld-malfunctions";
import { useActionPermissions } from "../../common/auth/useActionPermissions";
import classnames from "classnames";
import tenantRegistrations from "../master/tenant-registrations";
import masterFleetManagers from "../master/fleet-managers";
import trailerGpsProviders from "../administration/trailer-gps-providers";
import apiClients from "../administration/api-clients";
import { AssignmentLateOutlined, DashboardTwoTone } from "@material-ui/icons";
import geometrisApi from "../administration/geometris-api";
import safety from "../safety";
import mobileAppVersions from "../master/mobile-app-versions";
import fuelProviders from "../administration/fuel-providers";
import { AppBarFiltersContainerContext } from "../common/app-bar-filters/AppBarFiltersContainerContext";
import demoAccounts from "../master/demo-accounts";
import virtualTenants from "../master/virtual-tenants";
import ifta from "../locations/ifta";

enum MenuName {
  HOS = "hos",
  SAFETY = "safety",
  LOCATIONS = "locations",
  ASSETS = "assets",
  REPORTS = "reports",
  ADMINISTRATION = "administration",
  TEST = "test",
}

type ActionType = "toggle";

interface IAction {
  type: ActionType;
  payload: MenuName;
}

interface Props {
  dense: boolean;
  logout: () => void;
  onMenuClick: () => void;
}

interface IToggleState {
  hos: boolean;
  safety: boolean;
  locations: boolean;
  assets: boolean;
  reports: boolean;
  administration: boolean;
  test: boolean;
  [key: string]: boolean | null;
}

const initialState: IToggleState = {
  hos: false,
  safety: false,
  locations: false,
  assets: false,
  reports: false,
  administration: false,
  test: false,
};

const toggleStateReducer: React.Reducer<IToggleState, IAction> = (
  prevState,
  action
) => {
  const retVal = {
    ...initialState,
    [action.payload]: !prevState[action.payload],
  };
  return retVal;
};

const useStyles = makeStyles<Theme>((theme) => ({
  root: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  open: {
    width: 240,
  },
  closed: {
    width: 55,
  },
  menuItemActive: {
    color: theme.palette.text.primary,
    fill: theme.palette.info.main,
    background:
      theme.palette.type === "dark"
        ? alpha(theme.palette.grey[700], 0.5)
        : alpha(theme.palette.grey[400], 0.5),
  },
}));

const Menu: FC<Props> = ({ dense, logout }) => {
  const permissions = useActionPermissions();
  const { container, setContainer } = useContext(AppBarFiltersContainerContext);
  const containerCallback = useCallback(
    (ref) => {
      if (setContainer && ref) {
        setContainer(ref);
      }
    },
    [setContainer]
  );

  const [toggleState, dispatchToggleState] = useReducer(
    toggleStateReducer,
    initialState
  );
  const translate = useTranslate();
  const isXSmall = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("xs")
  );
  const showUserOptions = useMediaQuery("(max-width:992px)");
  const open = useSelector((state: AppState) => state.admin.ui.sidebarOpen);
  useSelector((state: AppState) => state.theme); // force rerender on theme change

  const themeName = useSelector((state: AppState) => state.theme);

  const classes = useStyles();

  const handleToggle = (menu: MenuName) => {
    dispatchToggleState({ type: "toggle", payload: menu });
  };

  const location = useLocation();

  useEffect(() => {
    if (toggleState !== initialState) {
      return;
    }

    if (location.pathname.startsWith("/hos")) {
      dispatchToggleState({ type: "toggle", payload: MenuName.HOS });
    }
    if (location.pathname.startsWith("/safety")) {
      dispatchToggleState({ type: "toggle", payload: MenuName.SAFETY });
    }
    if (location.pathname.startsWith("/locations")) {
      dispatchToggleState({ type: "toggle", payload: MenuName.LOCATIONS });
    }
    if (location.pathname.startsWith("/assets")) {
      dispatchToggleState({ type: "toggle", payload: MenuName.ASSETS });
    }
    if (location.pathname.startsWith("/reports")) {
      dispatchToggleState({ type: "toggle", payload: MenuName.REPORTS });
    }
    if (location.pathname.startsWith("/admin")) {
      dispatchToggleState({ type: "toggle", payload: MenuName.ADMINISTRATION });
    }
  }, [location, toggleState]);

  return (
    <div
      className={classnames("left-menu-container", classes.root, {
        [classes.open]: open,
        [classes.closed]: !open,
      })}
    >
      {open && showUserOptions && (
        <div
          className={`appbar-filters-container-mobile ${
            themeName === "dark" ? "appbar-filters-container-mobile-dark" : ""
          }`}
          id="appbar-filters-container"
          ref={containerCallback}
        ></div>
      )}
      {open && showUserOptions && container && (
        <Divider orientation="horizontal" variant="fullWidth" />
      )}
      {permissions && permissions[permissionNames.locations.current.main] && (
        <DashboardMenuItem onClick={() => {}} sidebarIsOpen={open} />
      )}
      {permissions &&
        (permissions[permissionNames.hos.drivers.main] ||
          permissions[permissionNames.hos.logs.main] ||
          permissions[permissionNames.hos.violations.main] ||
          permissions[permissionNames.hos.unidentifiedEvents.main] ||
          permissions[permissionNames.hos.eldDisconnections.main] ||
          permissions[permissionNames.hos.eldMalfunctions.main] ||
          permissions[permissionNames.hos.fmcsaDataTransfer.main]) &&
        !permissions[permissionNames.hos.logs.reassign] && (
          <SubMenu
            handleToggle={() => handleToggle(MenuName.HOS)}
            isOpen={toggleState.hos}
            sidebarIsOpen={open}
            name="menu.hos"
            icon={<hos.icon />}
            dense={dense}
          >
            {permissions[permissionNames.hos.drivers.main] &&
              // HACK: Avoid showing HOS - Drivers on master UI
              !permissions[permissionNames.hos.logs.reassign] && (
                <MenuItemLink
                  activeClassName={classes.menuItemActive}
                  to={`/${resourceNames.HOS_DRIVERS}`}
                  primaryText={translate(`resources.drivers.name`)}
                  leftIcon={<drivers.icon />}
                  onClick={() => {}}
                  sidebarIsOpen={open}
                  dense={dense}
                />
              )}
            {permissions[permissionNames.hos.logs.main] && (
              <MenuItemLink
                activeClassName={classes.menuItemActive}
                to={`/${resourceNames.HOS_LOGS}`}
                primaryText={translate("resources.hos-logs.name")}
                leftIcon={<logs.icon />}
                onClick={() => {}}
                sidebarIsOpen={open}
                dense={dense}
              />
            )}
            {permissions[permissionNames.hos.violations.main] &&
              !permissions[
                permissionNames.admin.tenantRegistrations.manage
              ] && (
                <MenuItemLink
                  activeClassName={classes.menuItemActive}
                  to={`/${resourceNames.HOS_RECENT_VIOLATIONS}`}
                  primaryText={translate("resources.hos-violations.name")}
                  leftIcon={<violations.icon />}
                  onClick={() => {}}
                  sidebarIsOpen={open}
                  dense={dense}
                />
              )}
            {permissions[permissionNames.hos.unidentifiedEvents.main] &&
              !permissions[
                permissionNames.admin.tenantRegistrations.manage
              ] && (
                <MenuItemLink
                  activeClassName={classes.menuItemActive}
                  to={`/${resourceNames.HOS_UNIDENTIFIED_DRIVING}`}
                  primaryText={translate(
                    "resources.hos-unidentified-driving.name"
                  )}
                  leftIcon={<unidentifiedDriving.icon />}
                  onClick={() => {}}
                  sidebarIsOpen={open}
                  dense={dense}
                />
              )}
            {permissions[permissionNames.hos.eldDisconnections.main] && (
              <MenuItemLink
                activeClassName={classes.menuItemActive}
                to={`/${resourceNames.HOS_ELD_DISCONNECTIONS}`}
                primaryText={translate("resources.eldDisconnections.name")}
                leftIcon={<eldDisconnections.icon />}
                onClick={() => {}}
                sidebarIsOpen={open}
                dense={dense}
              />
            )}
            {permissions[permissionNames.hos.eldMalfunctions.main] && (
              <MenuItemLink
                activeClassName={classes.menuItemActive}
                to={`/${resourceNames.HOS_ELD_MALFUNCTIONS}`}
                primaryText={translate("resources.hos-eld-malfunctions.name")}
                leftIcon={<eldMalfunctions.icon />}
                onClick={() => {}}
                sidebarIsOpen={open}
                dense={dense}
              />
            )}
            {permissions[permissionNames.hos.fmcsaDataTransfer.main] && (
              <MenuItemLink
                activeClassName={classes.menuItemActive}
                to={`/hos-fmcsa-data-transfer`}
                primaryText={translate("customPages.fmcsaDataTransfer.name")}
                leftIcon={<fmcsaDataTransfer.icon />}
                onClick={() => {}}
                sidebarIsOpen={open}
                dense={dense}
              />
            )}
          </SubMenu>
        )}
      {permissions && permissions[permissionNames.safety.alerts.main] && (
        <SubMenu
          handleToggle={() => handleToggle(MenuName.SAFETY)}
          isOpen={toggleState.safety}
          sidebarIsOpen={open}
          dense={dense}
          icon={<safety.icon />}
          name="menu.safety"
        >
          {permissions[permissionNames.safety.alerts.main] && (
            <MenuItemLink
              activeClassName={classes.menuItemActive}
              to={`/${resourceNames.SAFETY_DASHBOARD}`}
              primaryText="Dashboard"
              leftIcon={<DashboardTwoTone />}
              onClick={() => {}}
              sidebarIsOpen={open}
              dense={dense}
            />
          )}

          {permissions[permissionNames.safety.alerts.main] && (
            <MenuItemLink
              activeClassName={classes.menuItemActive}
              to={`/${resourceNames.SAFETY_ALERTS}`}
              primaryText="Alerts"
              leftIcon={<AssignmentLateOutlined />}
              onClick={() => {}}
              sidebarIsOpen={open}
              dense={dense}
            />
          )}
        </SubMenu>
      )}
      {
        // permissions && permissions[permissionNames.locations.current.main] && (
        // <SubMenu
        //   handleToggle={() => handleToggle(MenuName.LOCATIONS)}
        //   isOpen={toggleState.locations}
        //   sidebarIsOpen={open}
        //   dense={dense}
        //   icon={<locations.icon />}
        //   name="menu.locations"
        // >
        //   {permissions[permissionNames.locations.current.main] && (
        //     <MenuItemLink
        //       activeClassName={classes.menuItemActive}
        //       to={`/${resourceNames.LOCATIONS_CURRENT}`}
        //       primaryText="Dashboard"
        //       leftIcon={<DashboardTwoTone />}
        //       onClick={() => {}}
        //       sidebarIsOpen={open}
        //       dense={dense}
        //     />
        //   )}
        //   {permissions[permissionNames.locations.history.main] && (
        //     <MenuItemLink
        //       activeClassName={classes.menuItemActive}
        //       to={`/${resourceNames.LOCATIONS_STATE_TRIPS}`}
        //       primaryText="State trips (IFTA)"
        //       leftIcon={<ifta.icon />}
        //       onClick={() => {}}
        //       sidebarIsOpen={open}
        //       dense={dense}
        //     />
        //   )}
        //   {/* {permissions[permissionNames.locations.current.main] && (
        //       <MenuItemLink
        //         to={`/${resourceNames.LOCATIONS_TRAILERS}`}
        //         primaryText={translate("resources.locations-trailers.name")}
        //         leftIcon={<locationsCurrent.icon />}
        //         onClick={() => {}}
        //         sidebarIsOpen={open}
        //         dense={dense}
        //       />
        //     )}
        //     {permissions[permissionNames.locations.history.main] && (
        //       <MenuItemLink
        //         to={`/${resourceNames.LOCATIONS_HISTORY}`}
        //         primaryText={translate("resources.locations-history.name")}
        //         leftIcon={<locationsHistory.icon />}
        //         onClick={() => {}}
        //         sidebarIsOpen={open}
        //         dense={dense}
        //       />
        //     )} */}
        // </SubMenu>
        // )
      }
      {permissions[permissionNames.locations.history.main] && (
        <MenuItemLink
          activeClassName={classes.menuItemActive}
          to={`/${resourceNames.LOCATIONS_STATE_TRIPS}`}
          primaryText="State trips (IFTA)"
          leftIcon={<ifta.icon />}
          onClick={() => {}}
          sidebarIsOpen={open}
          dense={dense}
        />
      )}
      {permissions &&
        (permissions[permissionNames.assets.diagnostics.main] ||
          permissions[permissionNames.assets.maintenance.main] ||
          permissions[permissionNames.assets.dvirHistory.main]) && (
          <SubMenu
            handleToggle={() => handleToggle(MenuName.ASSETS)}
            isOpen={toggleState.assets}
            sidebarIsOpen={open}
            name="menu.assets"
            icon={<assets.icon />}
            dense={dense}
          >
            {permissions[permissionNames.assets.maintenance.view] && (
              <MenuItemLink
                activeClassName={classes.menuItemActive}
                to={`/${resourceNames.ASSETS_MAINTENANCE_DASHBOARD}`}
                primaryText={translate("resources.maintenance-dashboard.name")}
                leftIcon={<DashboardTwoTone />}
                onClick={() => {}}
                sidebarIsOpen={open}
                dense={dense}
              />
            )}
            {permissions[permissionNames.assets.dvirHistory.main] && (
              <MenuItemLink
                activeClassName={classes.menuItemActive}
                to={`/${resourceNames.ASSETS_DVIR_HISTORY}`}
                primaryText={translate("resources.assets-dvir-history.name")}
                leftIcon={<dvir.icon />}
                onClick={() => {}}
                sidebarIsOpen={open}
                dense={dense}
              />
            )}
            {permissions[permissionNames.assets.maintenance.main] && (
              <MenuItemLink
                activeClassName={classes.menuItemActive}
                to={`/${resourceNames.ASSETS_MAINTENANCE}`}
                primaryText={translate("resources.assets-maintenance.name")}
                leftIcon={<maintenance.icon />}
                onClick={() => {}}
                sidebarIsOpen={open}
                dense={dense}
              />
            )}
            {permissions[permissionNames.assets.diagnostics.main] && (
              <MenuItemLink
                activeClassName={classes.menuItemActive}
                to={`/${resourceNames.ASSETS_MALFUNCTIONS_DIAG}`}
                primaryText={translate("resources.malfunctionsDiag.name")}
                leftIcon={<malfunctionsDiag.icon />}
                onClick={() => {}}
                sidebarIsOpen={open}
                dense={dense}
              />
            )}
          </SubMenu>
        )}
      {permissions && permissions[permissionNames.reports.general.main] && (
        <MenuItemLink
          to="/reports"
          activeClassName={classes.menuItemActive}
          primaryText={translate("menu.reports")}
          leftIcon={<reports.icon />}
          onClick={() => {}}
          sidebarIsOpen={open}
          dense={dense}
        />
      )}
      {permissions &&
        permissions[permissionNames.admin.tenantRegistrations.main] && (
          <MenuItemLink
            to={`/${resourceNames.MASTER_TENANT_REGISTRATIONS}`}
            activeClassName={classes.menuItemActive}
            primaryText={translate(
              `resources.${resourceNames.MASTER_TENANT_REGISTRATIONS}.name`
            )}
            leftIcon={<tenantRegistrations.icon />}
            onClick={() => {}}
            sidebarIsOpen={open}
            dense={dense}
          />
        )}
      {permissions &&
        permissions[permissionNames.master.subscriptions.main] && (
          <MenuItemLink
            activeClassName={classes.menuItemActive}
            to={`/${resourceNames.MASTER_SUBSCRIPTIONS}`}
            primaryText={translate(
              `resources.${resourceNames.MASTER_SUBSCRIPTIONS}.name`
            )}
            leftIcon={<subscriptions.icon />}
            onClick={() => {}}
            sidebarIsOpen={open}
            dense={dense}
          />
        )}
      {permissions &&
        (permissions[permissionNames.admin.companies.main] ||
          permissions[permissionNames.admin.fleetManagers.main] ||
          permissions[permissionNames.admin.drivers.main] ||
          permissions[permissionNames.admin.vehicles.main] ||
          permissions[permissionNames.admin.trailers.main] ||
          permissions[permissionNames.admin.groups.main] ||
          permissions[permissionNames.admin.elds.main] ||
          permissions[permissionNames.admin.roles.main] ||
          permissions[permissionNames.admin.dvirForms.main] ||
          permissions[permissionNames.admin.notifications.main] ||
          permissions[permissionNames.admin.apiClients.main] ||
          permissions[permissionNames.admin.masterElds.main] ||
          permissions[permissionNames.admin.masterElds.sendCommands] ||
          permissions[permissionNames.admin.masterFleetManagers.main]) && (
          <SubMenu
            handleToggle={() => handleToggle(MenuName.ADMINISTRATION)}
            isOpen={toggleState.administration}
            sidebarIsOpen={open}
            name="menu.administration"
            icon={<administration.icon />}
            dense={dense}
          >
            {permissions[permissionNames.admin.companies.main] && (
              <MenuItemLink
                activeClassName={classes.menuItemActive}
                to={`/${resourceNames.ADMIN_COMPANIES}`}
                primaryText={translate(
                  `resources.${resourceNames.ADMIN_COMPANIES}.name`
                )}
                leftIcon={<companies.icon />}
                onClick={() => {}}
                sidebarIsOpen={open}
                dense={dense}
              />
            )}
            {permissions[permissionNames.admin.fleetManagers.main] && (
              <MenuItemLink
                activeClassName={classes.menuItemActive}
                to={`/${resourceNames.ADMIN_FLEET_MANAGERS}`}
                primaryText={translate(
                  `resources.${resourceNames.ADMIN_FLEET_MANAGERS}.name`
                )}
                leftIcon={<fleetManagers.icon />}
                onClick={() => {}}
                sidebarIsOpen={open}
                dense={dense}
              />
            )}
            {permissions[permissionNames.admin.masterFleetManagers.main] && (
              <MenuItemLink
                activeClassName={classes.menuItemActive}
                to={`/${resourceNames.MASTER_FLEET_MANAGERS}`}
                primaryText={translate(
                  `resources.${resourceNames.MASTER_FLEET_MANAGERS}.name`
                )}
                leftIcon={<masterFleetManagers.icon />}
                onClick={() => {}}
                sidebarIsOpen={open}
                dense={dense}
              />
            )}

            {permissions[permissionNames.admin.drivers.main] && (
              <MenuItemLink
                activeClassName={classes.menuItemActive}
                to={`/${resourceNames.ADMIN_DRIVERS}`}
                primaryText={translate(
                  `resources.${resourceNames.ADMIN_DRIVERS}.name`
                )}
                leftIcon={<adminDrivers.icon />}
                onClick={() => {}}
                sidebarIsOpen={open}
                dense={dense}
              />
            )}
            {permissions[permissionNames.admin.vehicles.main] && (
              <MenuItemLink
                activeClassName={classes.menuItemActive}
                to={`/${resourceNames.ADMIN_VEHICLES}`}
                primaryText={translate(
                  `resources.${resourceNames.ADMIN_VEHICLES}.name`
                )}
                leftIcon={<vehicles.icon />}
                onClick={() => {}}
                sidebarIsOpen={open}
                dense={dense}
              />
            )}
            {permissions[permissionNames.admin.trailers.main] && (
              <MenuItemLink
                activeClassName={classes.menuItemActive}
                to={`/${resourceNames.ADMIN_TRAILERS}`}
                primaryText={translate(
                  `resources.${resourceNames.ADMIN_TRAILERS}.name`
                )}
                leftIcon={<trailers.icon />}
                onClick={() => {}}
                sidebarIsOpen={open}
                dense={dense}
              />
            )}
            {permissions[permissionNames.admin.trailerGpsProviders.main] && (
              <MenuItemLink
                activeClassName={classes.menuItemActive}
                to={`/${resourceNames.ADMIN_TRAILER_GPS_PROVIDERS}`}
                primaryText={translate(
                  `resources.${resourceNames.ADMIN_TRAILER_GPS_PROVIDERS}.name`
                )}
                leftIcon={<trailerGpsProviders.icon />}
                onClick={() => {}}
                sidebarIsOpen={open}
                dense={dense}
              />
            )}
            {permissions[permissionNames.admin.fuelProviders.main] && (
              <MenuItemLink
                activeClassName={classes.menuItemActive}
                to={`/${resourceNames.ADMIN_FUEL_PROVIDERS}`}
                primaryText={translate(
                  `resources.${resourceNames.ADMIN_FUEL_PROVIDERS}.name`
                )}
                leftIcon={<fuelProviders.icon />}
                onClick={() => {}}
                sidebarIsOpen={open}
                dense={dense}
              />
            )}
            {permissions[permissionNames.admin.groups.main] && (
              <MenuItemLink
                activeClassName={classes.menuItemActive}
                to={`/${resourceNames.ADMIN_GROUPS}`}
                primaryText={translate(
                  `resources.${resourceNames.ADMIN_GROUPS}.name`
                )}
                leftIcon={<groups.icon />}
                onClick={() => {}}
                sidebarIsOpen={open}
                dense={dense}
              />
            )}
            {permissions[permissionNames.admin.elds.main] && (
              <MenuItemLink
                activeClassName={classes.menuItemActive}
                to={`/${resourceNames.ADMIN_ELDS}`}
                primaryText={translate(
                  `resources.${resourceNames.ADMIN_ELDS}.name`
                )}
                leftIcon={<adminElds.icon />}
                onClick={() => {}}
                sidebarIsOpen={open}
                dense={dense}
              />
            )}
            {permissions[permissionNames.admin.roles.main] && (
              <MenuItemLink
                activeClassName={classes.menuItemActive}
                to={`/${resourceNames.ADMIN_ROLES}`}
                primaryText={translate(
                  `resources.${resourceNames.ADMIN_ROLES}.name`
                )}
                leftIcon={<roles.icon />}
                onClick={() => {}}
                sidebarIsOpen={open}
                dense={dense}
              />
            )}
            {permissions[permissionNames.admin.notifications.main] && (
              <MenuItemLink
                activeClassName={classes.menuItemActive}
                to={`/${resourceNames.ADMIN_NOTIFICATIONS}`}
                primaryText={translate(
                  `resources.${resourceNames.ADMIN_NOTIFICATIONS}.name`
                )}
                leftIcon={<notifications.icon />}
                onClick={() => {}}
                sidebarIsOpen={open}
                dense={dense}
              />
            )}
            {permissions[permissionNames.admin.dvirForms.main] && (
              <MenuItemLink
                activeClassName={classes.menuItemActive}
                to={`/${resourceNames.ADMIN_DVIR_FORMS}`}
                primaryText={translate(
                  `resources.${resourceNames.ADMIN_DVIR_FORMS}.name`
                )}
                leftIcon={<dvirForms.icon />}
                onClick={() => {}}
                sidebarIsOpen={open}
                dense={dense}
              />
            )}
            {permissions[permissionNames.admin.apiClients.main] && (
              <MenuItemLink
                activeClassName={classes.menuItemActive}
                to={`/${resourceNames.ADMIN_API_CLIENTS}`}
                primaryText={translate(
                  `resources.${resourceNames.ADMIN_API_CLIENTS}.name`
                )}
                leftIcon={<apiClients.icon />}
                onClick={() => {}}
                sidebarIsOpen={open}
                dense={dense}
              />
            )}

            {permissions &&
              permissions[permissionNames.admin.masterElds.main] && (
                <MenuItemLink
                  activeClassName={classes.menuItemActive}
                  to={`/${resourceNames.MASTER_ELDS}`}
                  primaryText={translate(
                    `resources.${resourceNames.MASTER_ELDS}.name`
                  )}
                  leftIcon={<elds.icon />}
                  onClick={() => {}}
                  sidebarIsOpen={open}
                  dense={dense}
                />
              )}
            {permissions &&
              permissions[permissionNames.admin.masterElds.main] && (
                <MenuItemLink
                  activeClassName={classes.menuItemActive}
                  to={`/${resourceNames.MASTER_TENANT_OWN_ELDS}`}
                  primaryText={translate(
                    `resources.${resourceNames.MASTER_TENANT_OWN_ELDS}.name`
                  )}
                  leftIcon={<elds.icon />}
                  onClick={() => {}}
                  sidebarIsOpen={open}
                  dense={dense}
                />
              )}
            {permissions &&
              permissions[permissionNames.admin.masterElds.sendCommands] && (
                <MenuItemLink
                  activeClassName={classes.menuItemActive}
                  to={`/${resourceNames.ADMIN_GEOMETRIS_COMMANDS}`}
                  primaryText={translate(
                    `resources.${resourceNames.ADMIN_GEOMETRIS_COMMANDS}.name`
                  )}
                  leftIcon={<geometrisApi.icon />}
                  onClick={() => {}}
                  sidebarIsOpen={open}
                  dense={dense}
                />
              )}
            {permissions &&
              permissions[permissionNames.admin.mobileAppVersions.main] && (
                <MenuItemLink
                  activeClassName={classes.menuItemActive}
                  to={`/${resourceNames.ADMIN_MOBILE_APP_VERSIONS}`}
                  primaryText="Mobile app versions"
                  leftIcon={<mobileAppVersions.icon />}
                  onClick={() => {}}
                  sidebarIsOpen={open}
                  dense={dense}
                />
              )}
            {permissions &&
              permissions[permissionNames.admin.demoAccounts.main] && (
                <MenuItemLink
                  activeClassName={classes.menuItemActive}
                  to={`/${resourceNames.MASTER_DEMO_ACCOUNTS}`}
                  primaryText="Demo accounts"
                  leftIcon={<demoAccounts.icon />}
                  onClick={() => {}}
                  sidebarIsOpen={open}
                  dense={dense}
                />
              )}
            {permissions &&
              permissions[permissionNames.admin.virtualTenants.main] && (
                <MenuItemLink
                  activeClassName={classes.menuItemActive}
                  to={`/${resourceNames.MASTER_VIRTUAL_TENANTS}`}
                  primaryText="Virtual customers"
                  leftIcon={<virtualTenants.icon />}
                  onClick={() => {}}
                  sidebarIsOpen={open}
                  dense={dense}
                />
              )}
          </SubMenu>
        )}
      {isXSmall && logout}
    </div>
  );
};

export default Menu;
